import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { apiGet } from '@/api/client';
import type { Company, Pilot, PilotStatus } from '@/api/types';
import { AsyncSection } from '@/app/components/AsyncSection';
import { Badge } from '@/app/components/ui/Badge';
import { DataTable } from '@/app/components/ui/DataTable';
import { EmptyState } from '@/app/components/ui/EmptyState';
import { Field, Select } from '@/app/components/ui/Field';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { useAsync } from '@/lib/useAsync';

const STATUSES: readonly PilotStatus[] = [
  'draft',
  'baseline',
  'active',
  'interim_review',
  'final_review',
  'completed',
  'scaled',
];

function statusTone(status: PilotStatus | undefined): 'mint' | 'outline' {
  return status === 'active' || status === 'interim_review' || status === 'final_review'
    ? 'mint'
    : 'outline';
}

/**
 * "Beheer › Pilots" (PLAN-FASE2.md WP-07): every pilot on the platform, one
 * table, filterable by company and status. Reuses WP-04's `GET /pilots`
 * unchanged on the frontend — dropping `company_id` gets the platform-wide
 * list the API now serves scoped by role itself (an admin sees every
 * company's pilots; a company_admin only their own company's). The company
 * and status filters below are client-side narrowing of that one already-
 * scoped response, not separate requests.
 */
export function PilotsOverviewPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [companyFilter, setCompanyFilter] = useState(searchParams.get('company') ?? '');
  const [statusFilter, setStatusFilter] = useState('');

  const pilots = useAsync(() => apiGet('/pilots') as Promise<Pilot[]>, []);
  const companies = useAsync(() => apiGet('/companies') as Promise<Company[]>, []);

  const companyNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const company of companies.data ?? []) {
      if (company.id && company.name) map.set(company.id, company.name);
    }
    return map;
  }, [companies.data]);

  const filteredRows = useMemo(() => {
    const rows = pilots.data ?? [];
    return rows.filter((pilot) => {
      if (companyFilter !== '' && pilot.company_id !== companyFilter) return false;
      if (statusFilter !== '' && pilot.status !== statusFilter) return false;
      return true;
    });
  }, [pilots.data, companyFilter, statusFilter]);

  const companyOptions = useMemo(() => {
    const ids = new Set<string>();
    for (const pilot of pilots.data ?? []) {
      if (pilot.company_id) ids.add(pilot.company_id);
    }
    return [...ids].sort((a, b) =>
      (companyNames.get(a) ?? a).localeCompare(companyNames.get(b) ?? b),
    );
  }, [pilots.data, companyNames]);

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={t('managePilots.page.title')} lead={t('managePilots.page.lead')} />

      <form className="flex flex-wrap items-end gap-3">
        <Field label={t('managePilots.page.filterCompany')} className="w-64">
          <Select value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)}>
            <option value="">{t('managePilots.page.allCompanies')}</option>
            {companyOptions.map((id) => (
              <option key={id} value={id}>
                {companyNames.get(id) ?? id}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('managePilots.page.filterStatus')} className="w-56">
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">{t('managePilots.page.allStatuses')}</option>
            {STATUSES.map((status) => (
              <option key={status} value={status}>
                {t(`managePilots.status.${status}`)}
              </option>
            ))}
          </Select>
        </Field>
      </form>

      <AsyncSection state={pilots} label={t('managePilots.page.rowsLabel')} skeleton="table">
        {() =>
          filteredRows.length === 0 ? (
            <EmptyState text={t('managePilots.page.empty')} />
          ) : (
            <DataTable
              rowKey={(pilot) => pilot.id ?? ''}
              rows={filteredRows}
              columns={[
                {
                  key: 'company',
                  header: t('managePilots.page.company'),
                  render: (pilot) => (
                    <Link to={`/manage/companies/${pilot.company_id}`}>
                      {(pilot.company_id && companyNames.get(pilot.company_id)) ?? pilot.company_id}
                    </Link>
                  ),
                },
                {
                  key: 'name',
                  header: t('managePilots.page.name'),
                  render: (pilot) => pilot.name,
                },
                {
                  key: 'status',
                  header: t('managePilots.page.status'),
                  render: (pilot) => (
                    <Badge tone={statusTone(pilot.status)}>
                      {pilot.status ? t(`managePilots.status.${pilot.status}`) : '—'}
                    </Badge>
                  ),
                },
                {
                  key: 'startDate',
                  header: t('managePilots.page.startDate'),
                  render: (pilot) => pilot.start_date ?? '—',
                },
                {
                  key: 'endDate',
                  header: t('managePilots.page.endDate'),
                  render: (pilot) => pilot.end_date ?? '—',
                },
                {
                  key: 'departments',
                  header: t('managePilots.page.departments'),
                  render: (pilot) => pilot.department_ids?.length ?? 0,
                },
              ]}
            />
          )
        }
      </AsyncSection>
    </section>
  );
}
